import { WEB_SEARCH_PROVIDERS } from "@rally/agreed";
import {
  Alert,
  BrandMark,
  Button,
  breakpoints,
  Col,
  Field,
  Icon,
  Input,
  ListRow,
  ScrollArea,
  Select,
  Spinner,
  Stack,
  Status,
  Surface,
  Text,
  useConfirm,
  useTheme,
} from "@rally/ui-kit";
import { useState } from "react";
import { howLongAgo } from "../localization/format.ts";
import { say } from "../localization/say.ts";
import { act } from "../net/rally.ts";
import type { SearchSubject } from "./an-integration.ts";
import type { HeldSearchKey } from "./held.ts";
import { saidWhy } from "./signing-in.ts";

function looksPasteable(secret: string): boolean {
  return secret.trim().length >= 16 && !/\s/.test(secret.trim());
}

export function SearchKeys({
  held: everyHeld,
  loading,
  onChanged,
  only,
}: {
  held: HeldSearchKey[];
  loading: boolean;
  onChanged: () => void;
  only?: SearchSubject;
}) {
  const theme = useTheme();
  const held = only ? everyHeld.filter((one) => one.provider === only.provider) : everyHeld;
  const [provider, setProvider] = useState<string>(only?.provider ?? WEB_SEARCH_PROVIDERS[0]);
  const title = only ? only.name : say("search.title");
  const lead = only ? (
    only.mark ? (
      <BrandMark name={only.mark} size={22} tone="brand" />
    ) : (
      <Icon name={only.icon ?? "search"} size={22} color="text.secondary" />
    )
  ) : (
    <Icon name="globe" size={22} color="text.secondary" />
  );
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const { ask, dialog } = useConfirm();

  async function send(): Promise<void> {
    if (!looksPasteable(secret)) return setWrong(say("search.notAKey"));
    setBusy(true);
    setWrong(null);
    try {
      await act("provider.addSearch", { provider, secret: secret.trim() });
      onChanged();
    } catch (cause) {
      setWrong(saidWhy(cause));
    } finally {
      setSecret("");
      setBusy(false);
    }
  }

  async function drop(id: string): Promise<void> {
    const sure = await ask({
      title: say("providers.removeTitle"),
      description: say("providers.removeMeans"),
      confirmLabel: say("providers.remove"),
      cancelLabel: say("providers.keep"),
      tone: "danger",
    });
    if (!sure) return;
    setWrong(null);
    try {
      await act("provider.remove", { credential: id });
      onChanged();
    } catch (cause) {
      setWrong(saidWhy(cause));
    }
  }

  return (
    <>
      <ListRow
        testID={only ? `search-keys-${only.provider}` : "search-keys"}
        lead={lead}
        title={title}
        value={
          held.length > 0
            ? only
              ? `${held[0]?.maskedHint ?? ""} · ${howLongAgo(held[0]?.addedAt ?? "")}`
              : say("search.storedMany")(held.length)
            : only
              ? say(`integrations.search.${only.provider}`)
              : say("search.what")
        }
        action={
          <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
            {held.length > 0 ? (
              <Status tone="success">{say("providers.held")}</Status>
            ) : theme.isAtMost(breakpoints.phone) ? null : (
              <Text typeName="caption" color="text.tertiary">
                {say("providers.notSetUp")}
              </Text>
            )}
            <Icon name="chevron-right" size={16} color="text.tertiary" />
          </Col>
        }
        onPress={() => setOpen(true)}
      />
      <Surface
        kind="panel"
        width={520}
        visible={open}
        onDismiss={() => setOpen(false)}
        title={title}
        testID={only ? `search-keys-panel-${only.provider}` : "search-keys-panel"}
      >
        <ScrollArea contentContainerStyle={{ padding: theme.space[5] }}>
          <Stack gap={4}>
            {dialog}
            <Text typeName="caption" color="text.secondary">
              {only ? say(`integrations.search.${only.provider}`) : say("search.what")}
            </Text>
            {loading ? <Spinner label={say("search.loading")} /> : null}
            {held.map((one, index) => (
              <ListRow
                key={one.id}
                testID={`search-key-${one.provider}`}
                title={one.provider}
                value={`${one.maskedHint} · ${howLongAgo(one.addedAt)}`}
                action={
                  <Button
                    size="sm"
                    variant="ghost"
                    onPress={() => void drop(one.id)}
                    testID="search-key-remove"
                  >
                    {say("search.remove")}
                  </Button>
                }
                last={index === held.length - 1}
              />
            ))}
            {!loading && held.length === 0 ? (
              <Text typeName="caption" color="warning" testID="search-key-none">
                {say("search.none")}
              </Text>
            ) : null}
            {wrong ? <Alert tone="danger">{wrong}</Alert> : null}
            {only ? null : (
              <Field label={say("search.provider")}>
                <Select
                  value={provider}
                  onChange={setProvider}
                  options={WEB_SEARCH_PROVIDERS.map((one) => ({ value: one, label: one }))}
                />
              </Field>
            )}
            <Field label={say("providers.key")} hint={say("providers.keyHint")}>
              <Input
                value={secret}
                onChangeText={setSecret}
                secureTextEntry
                testID="search-key-secret"
                onSubmitEditing={() => void send()}
              />
            </Field>
            <Col style={{ flexDirection: "row" }}>
              <Button loading={busy} onPress={() => void send()} testID="search-key-add">
                {say("search.add")}
              </Button>
            </Col>
          </Stack>
        </ScrollArea>
      </Surface>
    </>
  );
}
